import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BadgeCheck, ShoppingBag, Loader2, Truck, ShieldCheck, Undo2, Globe2 } from 'lucide-react';
import { market, PARTNER_LABELS } from '../lib/api';
import { useCart } from '../lib/cart';
import WishlistButton from '../components/WishlistButton';

const rs = (n) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function Offer({ o, p, best }) {
  const cart = useCart();
  const add = () => {
    cart.indiaAdd(o.store, { id: o.id, name: p.name, price: o.price, unit: p.unit || 'pc', image_url: p.images?.[0], stock: o.stock, gst_rate: p.gst_rate, mrp: p.mrp, slug: p.slug });
    toast.success(`Added from ${o.store.name}`);
  };
  return (
    <div data-testid={`offer-${o.id}`} className={`rounded-xl border p-3 flex flex-wrap items-center gap-3 ${best ? 'border-[#E67E22] bg-[#FFF8EE]' : 'border-border bg-white'}`}>
      <div className="flex-1 min-w-[160px]">
        <Link to={`/stores/${o.store.id}`} className="font-semibold hover:underline">{o.store.name}</Link>
        <div className="text-xs text-ink/60 flex flex-wrap gap-2 mt-0.5">
          {o.official_store ? <span className="inline-flex items-center gap-1 text-[#1B5E20] font-bold"><BadgeCheck className="w-3.5 h-3.5"/>Official brand store</span>
            : <span>{PARTNER_LABELS[o.seller_type] || 'Local shop'}</span>}
          <span className="inline-flex items-center gap-1"><Truck className="w-3 h-3"/>Ships in {o.dispatch_days ?? 2} days</span>
          {o.stock < 10 && <span className="text-[#B71C1C]">Only {o.stock} left</span>}
        </div>
      </div>
      <div className="font-heading font-bold text-xl" data-testid={`offer-price-${o.id}`}>{rs(o.price)}</div>
      <button data-testid={`offer-add-${o.id}`} onClick={add} className="btn-primary text-sm"><ShoppingBag className="w-4 h-4"/>Add to cart</button>
    </div>
  );
}

export default function CatalogProduct() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [img, setImg] = useState(0);
  useEffect(() => { market.product(slug).then(setP).catch(() => setErr(true)); }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto p-10 text-center" data-testid="catalog-product-missing">Product not found.</div>;
  if (!p) return <Loader2 className="w-6 h-6 animate-spin m-10 text-[#E67E22]"/>;
  const best = p.offers[0];
  const specs = Object.entries(p.specs || {});
  const dims = [p.length_cm, p.width_cm, p.height_cm].every(Boolean) ? `${p.length_cm} × ${p.width_cm} × ${p.height_cm} cm` : null;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 grid md:grid-cols-2 gap-8" data-testid="catalog-product-page">
      <div className="space-y-3">
        <div className="aspect-square rounded-2xl bg-white border border-border overflow-hidden relative">
          <WishlistButton cpId={p.id} className="absolute top-3 right-3 z-10"/><img src={p.images?.[img]} alt={p.name} className="w-full h-full object-contain"/></div>
        <div className="flex gap-2 overflow-x-auto">{(p.images || []).map((u, i) => (
          <button key={u} onClick={() => setImg(i)} className={`w-16 h-16 rounded-lg border-2 overflow-hidden shrink-0 ${i === img ? 'border-[#E67E22]' : 'border-border'}`}><img src={u} alt="" className="w-full h-full object-cover"/></button>
        ))}</div>
        {p.video_url && <a href={p.video_url} target="_blank" rel="noreferrer" className="text-sm underline text-[#D35400]">Watch product video</a>}
      </div>
      <div className="space-y-4">
        <div>
          {p.brand_info ? <Link to={`/brands/${p.brand_info.slug}`} data-testid="product-brand-link" className="text-sm font-semibold text-[#D35400] inline-flex items-center gap-1">{p.brand}<BadgeCheck className="w-4 h-4 text-[#1B5E20]"/></Link>
            : <span className="text-sm font-semibold text-ink/70">{p.brand}</span>}
          <h1 data-testid="catalog-product-name" className="font-heading font-black text-2xl md:text-3xl mt-1">{p.name}</h1>
          {p.manufacturer && <div className="text-sm text-ink/60">Manufactured by {p.manufacturer}</div>}
        </div>
        {best ? (
          <div className="flex items-baseline gap-3">
            <span className="font-heading font-black text-3xl">{rs(best.price)}</span>
            {p.mrp > best.price && <><span className="line-through text-ink/50">MRP {rs(p.mrp)}</span><span className="font-bold text-[#1B5E20]">{Math.round(((p.mrp - best.price) / p.mrp) * 100)}% off</span></>}
          </div>
        ) : <div className="text-ink/60">Out of stock with all sellers</div>}
        <div className="text-xs text-ink/60">Inclusive of GST ({p.gst_rate}%) · HSN {p.hsn}</div>
        <div className="flex flex-wrap gap-3 text-sm">
          <span className="pill"><Globe2 className="w-3.5 h-3.5"/>Made in {p.country_of_origin}</span>
          {p.warranty && <span className="pill"><ShieldCheck className="w-3.5 h-3.5"/>{p.warranty}</span>}
          <span className="pill"><Undo2 className="w-3.5 h-3.5"/>{p.returnable ? 'Returnable' : 'Not returnable'}</span>
        </div>
        <section className="space-y-2" data-testid="offers-list">
          <div className="font-heading font-bold">{p.offers.length} seller{p.offers.length === 1 ? '' : 's'}</div>
          {p.offers.map((o, i) => <Offer key={o.id} o={o} p={p} best={i === 0}/>)}
        </section>
        {p.description && <p className="text-sm text-ink/80 whitespace-pre-line">{p.description}</p>}
        {(specs.length > 0 || p.weight_g || dims) && (
          <table className="w-full text-sm" data-testid="product-specs">
            <tbody>
              {specs.map(([k, v]) => <tr key={k} className="border-t border-border"><td className="py-1.5 text-ink/60 w-1/3">{k}</td><td>{v}</td></tr>)}
              {p.weight_g && <tr className="border-t border-border"><td className="py-1.5 text-ink/60">Shipping weight</td><td>{p.weight_g} g</td></tr>}
              {dims && <tr className="border-t border-border"><td className="py-1.5 text-ink/60">Dimensions</td><td>{dims}</td></tr>}
              {p.barcode && <tr className="border-t border-border"><td className="py-1.5 text-ink/60">Barcode</td><td>{p.barcode}</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
